import type { ClaimExpenseItem } from "@/features/claims/components/expenses-list";

import type { AvailableCounterparty } from "../api/use-payment-orders";

export type FamilyAction =
  | "generate-receipt"
  | "send-email"
  | "create-order"
  | "pending-expenses"
  | "awaiting-signature"
  | "signed";

/**
 * Expenses belonging to a given family, resolved via the
 * FamilyClaimAcquisition → family → usersClaims → UserClaimExpense path.
 * This is always populated, unlike belongsToFamilyClaimAcquisitionId which
 * is nullable. Only returns expenses tied to a monetary demand
 * (EXPENSE_BACKED demands — EC261 assistance duty / MC99 damages).
 */
export function expensesForFamily(
  counterparty: AvailableCounterparty,
  claimExpenses: ClaimExpenseItem[],
): ClaimExpenseItem[] {
  const usersClaims = counterparty.familyClaimAcquisition?.family?.usersClaims ?? [];
  const familyExpenseIds = new Set(
    usersClaims.flatMap((userClaim) => (userClaim.expenses ?? []).map((expense) => expense.expenseId)),
  );
  if (!familyExpenseIds.size) {
    return [];
  }
  return claimExpenses.filter(
    (claimExpense) =>
      familyExpenseIds.has(claimExpense.expenseId) && claimExpense.expense.claimMonetaryDemandId != null,
  );
}

/** Mirrors TravelerPayments.familyAction (Vue) — which state-driven action a family card exposes. */
export function familyAction(
  counterparty: AvailableCounterparty,
  claimExpenses: ClaimExpenseItem[],
): FamilyAction {
  // Has signed receipt → ready to pay
  if (counterparty.receiptDocumentId) {
    return "create-order";
  }
  // Step is PAYMENT → receipt sent, awaiting passenger signature
  if (counterparty.familyClaimAcquisition?.step === "PAYMENT") {
    return "awaiting-signature";
  }
  if (counterparty.familyClaimAcquisition?.step === "PAYMENT_COMPLETE") {
    return "signed";
  }
  // Has draft but not signed → send the email
  if (counterparty.draftReceiptFileId) {
    return "send-email";
  }
  // Check if any family expense is still PENDING
  const familyExpenses = expensesForFamily(counterparty, claimExpenses);
  if (
    familyExpenses.length &&
    familyExpenses.some((claimExpense) => (claimExpense.expense.reimbursalStatus ?? "PENDING") === "PENDING")
  ) {
    return "pending-expenses";
  }
  // All expenses decided (or no expenses at all) → generate receipt
  return "generate-receipt";
}

/** Mirrors TravelerPayments.passengersLabel (Vue). */
export function passengersLabel(counterparty: AvailableCounterparty): string {
  const users = counterparty.familyClaimAcquisition?.family?.usersClaims ?? [];
  if (!users.length) {
    return "Famille sans passager actif";
  }
  return users.map((userClaim) => `Famille ${userClaim.user.lastName ?? ""}`.trim()).join(", ");
}
